import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * PageShell — the page container contract.
 *
 * Audit finding: admin pages layered six different width/padding regimes over
 * the shell (`p-6 space-y-6` double-padding, rogue `container max-w-*`
 * wrappers, bare divs), and /me pages spanned five max-widths. PageShell is
 * the one root every page renders into.
 *
 * Usage:
 *   Admin pages (the admin layout already provides p-4):
 *     <PageShell>…</PageShell>                      // full-bleed list page
 *     <PageShell width="lg">…</PageShell>           // centered settings page
 *   Standalone pages (/me, platform — no shell padding):
 *     <PageShell width="xl" padded>…</PageShell>
 *
 * Vertical rhythm is space-y-6 (the dominant convention). Don't add extra
 * root padding in pages — pick `padded` instead.
 */

const pageShellVariants = cva('w-full min-w-0 max-w-full', {
  variants: {
    width: {
      /** Full-bleed — heavy list/table pages inside the admin shell. */
      full: '',
      /** max-w-7xl — dashboards and wide detail pages. */
      xl: 'mx-auto max-w-7xl',
      /** max-w-5xl — settings and mid-width content. */
      lg: 'mx-auto max-w-5xl',
      /** max-w-4xl — forms and focused single-column pages. */
      md: 'mx-auto max-w-4xl',
      /** max-w-2xl — narrow reading/messaging columns. */
      sm: 'mx-auto max-w-2xl',
    },
    /** Adds the standalone padding for routes whose layout provides none
     *  (/me, platform). Admin pages must omit it — the admin shell pads. */
    padded: {
      true: 'px-4 py-6 sm:px-6',
      false: '',
    },
    /** Vertical rhythm. `none` is for pages whose content manages its own
     *  spacing (legacy mb-* rhythm) — prefer the default for new pages. */
    spacing: {
      default: 'space-y-6',
      none: '',
    },
  },
  defaultVariants: { width: 'full', padded: false, spacing: 'default' },
});

export interface PageShellProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof pageShellVariants> {}

const PageShell = React.forwardRef<HTMLDivElement, PageShellProps>(
  ({ className, width, padded, spacing, ...props }, ref) => (
    <div ref={ref} className={cn(pageShellVariants({ width, padded, spacing }), className)} {...props} />
  ),
);
PageShell.displayName = 'PageShell';

export { PageShell, pageShellVariants };
